// src/routes/__root.tsx
/// <reference types="vite/client" />
import type { ReactNode } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import appCss from '@/styles/app.css?url';
import NotFound from '@/components/Error404/NotFound';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { AuthContextProvider, AuthContextType } from '@/lib/auth';
import logo from '@/images/favicon.ico';
import CustomToast from '@/components/CustomToast';
import recordingViewCss from '@/styles/recording-view.css?url';
import { Analytics } from '@vercel/analytics/react';
import { PostHogProviderWrapper } from '@/lib/posthog';

export const Route = createRootRouteWithContext<{ auth: AuthContextType }>()({
  head: (e) => {
    const matches = e.matches
    const isRecordingView = matches.some(m => m.pathname === '/recording-view');
    return {
      meta: [
        {
          charSet: 'utf-8',
        },
        {
          name: 'viewport',
          content:
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
        },
        {
          title: 'GreetAI',
        },
      ],
      links: [
        {
          rel: 'stylesheet',
          href: isRecordingView ? recordingViewCss : appCss,
        },
        {
          rel: 'icon',
          href: logo,
        },
      ],
    };
  },
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const isMeetingPage = useRouterState({
    select: (state) => state.location.pathname.startsWith('/meeting/'),
  });
  // /embed runs inside customers' sites: no support chat bubble in their page.
  const isEmbed = useRouterState({
    select: (state) =>
      state.location.pathname.startsWith('/embed/') ||
      // A call opened inside the widget on a customer's site
      (state.location.pathname.startsWith('/meeting/') && state.location.searchStr.includes('embed=1')),
  });

  return (
    <RootDocument isEmbed={isEmbed}>
      <PostHogProviderWrapper>
        <QueryClientProvider client={queryClient}>
          <AuthContextProvider>
            <Outlet />
            <CustomToast dark={isMeetingPage} />
          </AuthContextProvider>
        </QueryClientProvider>
      </PostHogProviderWrapper>
    </RootDocument>
  );
}

function RootDocument({
  children,
  isEmbed = false,
}: Readonly<{ children: ReactNode; isEmbed?: boolean }>) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-TFCMFB82ZM"></script>
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-2B8DTJ5TG3"></script>
        <script async src="https://t.contentsquare.net/uxa/ddc0d81fbc71d.js"></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-TFCMFB82ZM');
            gtag('config', 'G-2B8DTJ5TG3');
          `,
          }}
        />
        {!isEmbed && <script
          dangerouslySetInnerHTML={{
            __html: `
            window.$crisp=[];window.CRISP_WEBSITE_ID="4549fe10-01fa-46c9-bc8d-48a191475c5d";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
            if(window.innerWidth<768){window.CRISP_READY_TRIGGER=function(){window.$crisp.push(["do","chat:hide"])};window.$crisp.push(["on","session:loaded",function(){if(window.innerWidth<768){window.$crisp.push(["do","chat:hide"])}}])}
          `,
          }}
        />}
      </head>
      <body>
        {children}
        <Analytics />
        <Scripts />
      </body>
    </html>
  );
}
